import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMe } from '@/hooks/use-auth';
import { absoluteTime, roleLabel } from '@/lib/utils';
import { Loader2, ShieldCheck } from 'lucide-react';

export const Route = createFileRoute('/_app/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { data, isLoading } = useMe();

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const user = data.user;

  return (
    <>
      <PageHeader title="Profile" description="Your account details and security settings." />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Identity</CardTitle>
            <CardDescription>How your account appears across the console.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Name" value={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '—'} />
            <Row label="Email" value={user.email} />
            <Row label="Role" value={<Badge variant="default">{roleLabel(user.role)}</Badge>} />
            <Row label="Organization" value={`${user.org.name} (${user.org.slug})`} />
            <Row label="Last login" value={absoluteTime(user.lastLoginAt)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              <span>Security</span>
            </CardTitle>
            <CardDescription>Manage your authentication factors.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row
              label="Two-factor"
              value={
                user.totpEnabled ? (
                  <Badge variant="success">Enabled</Badge>
                ) : (
                  <Badge variant="warning">Not enabled</Badge>
                )
              }
            />
            <p className="text-xs text-muted-foreground pt-2">
              Two-factor authentication and password change controls will land in Lot 2I.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-sm text-right">{value}</span>
    </div>
  );
}
